import React, { useState, useEffect, useCallback, memo, useMemo } from "react";
import "./AdminDashboard.css";

const API_URL = "https://threegor-1.onrender.com/api";

const TableRow = memo(({ item, activeTab, onEdit, onDelete, itemIndex }) => {
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const imageUrl = useMemo(() => {
    if (item.image) {
      return item.image.startsWith("http")
        ? item.image
        : `${API_URL.replace("/api", "")}/${item.image}`;
    }
    if (item.images && item.images[0]) {
      return item.images[0].startsWith("http")
        ? item.images[0]
        : `${API_URL.replace("/api", "")}/${item.images[0]}`;
    }
    return null;
  }, [item.image, item.images]);

  const placeholderSvg = useMemo(
    () =>
      `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Crect fill='%23e0e0e0' width='80' height='80'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='11' fill='%23999'%3ENo Image%3C/text%3E%3C/svg%3E`,
    []
  );

  return (
    <tr>
      <td>
        <div style={{ width: "80px", height: "80px", position: "relative" }}>
          {imageUrl && !imgError ? (
            <>
              {!imgLoaded && (
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    color: "#aaa",
                    position: "absolute",
                    top: 0,
                    left: 0,
                  }}
                >
                  ⏳
                </div>
              )}
              <img
                src={imageUrl}
                alt={item.title || "Item"}
                className="thumb"
                style={{
                  display: imgLoaded ? "block" : "none",
                  width: "80px",
                  height: "80px",
                  objectFit: "cover",
                  borderRadius: "12px",
                }}
                onLoad={() => setImgLoaded(true)}
                onError={() => {
                  setImgError(true);
                  setImgLoaded(false);
                }}
              />
            </>
          ) : (
            <img
              src={placeholderSvg}
              alt="No image"
              className="thumb"
              style={{ width: "80px", height: "80px", borderRadius: "12px" }}
            />
          )}
        </div>
      </td>
      <td>{item.title || "No title"}</td>
      <td>{activeTab === "portfolios" ? item.location : item.price || "-"}</td>
      <td>
        {activeTab === "portfolios"
          ? item.year
          : activeTab === "products"
            ? item.category
            : item.subtitle || "-"}
      </td>
      <td>
        <button
          className="edit-btn"
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onEdit(item);
          }}
        >
          ✏️ Edit
        </button>
        <button
          className="delete-btn"
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete(item._id);
          }}
        >
          🗑️ Delete
        </button>
      </td>
    </tr>
  );
});

TableRow.displayName = "TableRow";

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("promotions");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [originalImages, setOriginalImages] = useState(null);
  const [imageErrors, setImageErrors] = useState(new Set());
  const [formKey, setFormKey] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setImageErrors(new Set());
    try {
      const url = `${API_URL}/${activeTab}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const json = await res.json();
      const items = Array.isArray(json) ? json : json.data || [];
      setData(items);
    } catch (err) {
      console.error("Error fetching data:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInput = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleFile = useCallback((e) => {
    const { name, files } = e.target;
    if (files.length > 0) {
      setForm((prev) => ({
        ...prev,
        [name]: files.length > 1 ? Array.from(files) : files[0],
      }));
    }
  }, []);

  const handleEdit = useCallback((item) => {
    setForm({});
    setOriginalImages(null);
    setEditingId(null);
    setShowForm(false);
    setFormKey((prev) => prev + 1);

    setTimeout(() => {
      setEditingId(item._id);
      setShowForm(true);
      setOriginalImages({
        image: item.image || null,
        images: item.images || null,
      });
      const formData = { ...item };

      if (formData.features && Array.isArray(formData.features))
        formData.features = formData.features.join(", ");
      if(formData.detailedFeatures) {
        if (Array.isArray(formData.detailedFeatures)) {
          formData.detailedFeatures = formData.detailedFeatures.join("\n");
        } else if (typeof formData.detailedFeatures === "string") {
          formData.detailedFeatures = formData.detailedFeatures
            .split(/\n|,/)
            .map((v) => v.trim())
            .filter(Boolean)
            .join("\n");
        }
      }

      if (formData.materials && Array.isArray(formData.materials))
        formData.materials = formData.materials.join(", ");

      // ✅ parse "specifications" string into 3 fields for the form
      if (formData.specifications && typeof formData.specifications === "string") {
        const specs = formData.specifications.split(/\n|,/).map((v) => v.trim());
        const dimension =
          specs.find((s) => s.toLowerCase().startsWith("dimension"))?.split(":")[1]?.trim() || "";
        const material =
          specs.find((s) => s.toLowerCase().startsWith("material"))?.split(":")[1]?.trim() || "";
        const warranty =
          specs.find((s) => s.toLowerCase().startsWith("warranty"))?.split(":")[1]?.trim() || "";
        formData.dimension = dimension;
        formData.material = material;
        formData.warranty = warranty;
      }

      delete formData.image;
      delete formData.images;
      delete formData._id;
      delete formData.__v;
      delete formData.createdAt;
      delete formData.updatedAt;

      setForm(formData);
    }, 50);
  }, []);

  // ✅ handleSubmit: recombine dimension/material/warranty back to 1 string for backend
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setLoading(true);

      try {
        if (activeTab === "products") {
          const specsText = [
            form.dimension ? `Dimensions: ${form.dimension}` : null,
            form.material ? `Material: ${form.material}` : null,
            form.warranty ? `Warranty: ${form.warranty}` : null,
          ]
            .filter(Boolean)
            .join("\n");
          form.specifications = specsText;
        }

        const formData = new FormData();

        // Add all text fields
        Object.keys(form).forEach((key) => {
          if (!["image", "images"].includes(key) && form[key] !== undefined && form[key] !== null) {
            if (Array.isArray(form[key])) {
              formData.append(key, JSON.stringify(form[key]));
            } else if (typeof form[key] === "object" && !(form[key] instanceof File)) {
              formData.append(key, JSON.stringify(form[key]));
            } else {
              formData.append(key, form[key]);
            }
          }
        });

        // Images handling (unchanged)
        if (editingId) {
          if (form.image && form.image instanceof File) {
            formData.append("image", form.image);
          }
          if (form.images && form.images.length > 0) {
            if (Array.isArray(form.images)) {
              form.images.forEach((file) => {
                if (file instanceof File) {
                  formData.append("images", file);
                }
              });
            } else if (form.images instanceof FileList) {
              Array.from(form.images).forEach((file) => {
                formData.append("images", file);
              });
            }
          }
        } else {
          if (form.image instanceof File) {
            formData.append("image", form.image);
          }
          if (form.images) {
            if (Array.isArray(form.images)) {
              form.images.forEach((file) => {
                if (file instanceof File) {
                  formData.append("images", file);
                }
              });
            } else if (form.images instanceof FileList) {
              Array.from(form.images).forEach((file) => {
                formData.append("images", file);
              });
            }
          }
        }

        const url = editingId
          ? `${API_URL}/${activeTab}/${editingId}`
          : `${API_URL}/${activeTab}`;

        const res = await fetch(url, {
          method: editingId ? "PUT" : "POST",
          body: formData,
        });

        if (res.ok) {
          setShowForm(false);
          setEditingId(null);
          setForm({});
          setOriginalImages(null);
          setFormKey((prev) => prev + 1);
          await fetchData();
        } else {
          const errorData = await res.json();
          console.error("Error:", errorData);
          alert(`Error: ${errorData.message || "Failed to save"}`);
        }
      } catch (err) {
        console.error("Submit error:", err);
        alert("Failed to save. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [form, editingId, activeTab, fetchData]
  );

  const handleDelete = useCallback(
    async (id) => {
      if (!window.confirm("Are you sure you want to delete this item?")) return;
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/${activeTab}/${id}`, { method: "DELETE" });
        if (res.ok) await fetchData();
      } catch (err) {
        console.error("Delete error:", err);
      } finally {
        setLoading(false);
      }
    },
    [activeTab, fetchData]
  );

  const renderFormFields = () => {
    // -----------------------------
    // PRODUCTS (with fixed specs)
    // -----------------------------
    if (activeTab === "products") {
      return (
        <>
          <label>Title *</label>
          <input name="title" value={form.title || ""} onChange={handleInput} required />

          <label>Category</label>
          <select name="category" value={form.category || "kitchen"} onChange={handleInput}>
            <option value="kitchen">ชุดครัว (Kitchen)</option>
            <option value="tv-wall">ผนังทีวี (TV Wall)</option>
            <option value="closet">ตู้เสื้อผ้า (Closet)</option>
            <option value="furniture">เฟอร์นิเจอร์ (Furniture)</option>
          </select>

          <label>Price</label>
          <input name="price" type="number" value={form.price || ""} onChange={handleInput} placeholder="0" />

          <label>Description</label>
          <textarea
            name="fullDescription"
            value={form.fullDescription || ""}
            onChange={handleInput}
            rows="4"
            placeholder="Write a short description for this product..."
          />

          <label>Features (แยกบรรทัดละ 1 รายการ)</label>
          <textarea
            name="features"
            value={
              Array.isArray(form.features)
                ? form.features.join("\n")
                : (form.features || "")
                  .split(",")
                  .map((v) => v.trim())
                  .filter(Boolean)
                  .join("\n")
            }
            onChange={(e) => {
              const features = e.target.value
                .split(/\n|,/)
                .map((v) => v.trim())
                .filter(Boolean);
              setForm({ ...form, features });
            }}
            rows="4"
            placeholder="Durable Material\nCustom Design\nFree Installation"
          />

          {/* ✅ replaced old specifications textarea with 3 inputs */}
          <label>Dimension</label>
          <input
            name="dimension"
            value={form.dimension || ""}
            onChange={handleInput}
            placeholder="e.g., 200x60x80cm"
          />

          <label>Material</label>
          <input
            name="material"
            value={form.material || ""}
            onChange={handleInput}
            placeholder="e.g., Oak Wood"
          />

          <label>Warranty</label>
          <input
            name="warranty"
            value={form.warranty || ""}
            onChange={handleInput}
            placeholder="e.g., 5 years"
          />

          {editingId && originalImages && originalImages.image && (
            <div style={{ marginBottom: "15px", marginTop: "20px" }}>
              <label>Current Image:</label>
              <div style={{ margin: "10px 0" }}>
                <img
                  src={
                    originalImages.image.startsWith("http")
                      ? originalImages.image
                      : `${API_URL.replace("/api", "")}/${originalImages.image}`
                  }
                  alt="Current"
                  style={{
                    width: "150px",
                    height: "150px",
                    objectFit: "cover",
                    border: "2px solid #ddd",
                    borderRadius: "8px",
                  }}
                />
              </div>
              <p style={{ fontSize: "12px", color: "#666" }}>ℹ️ This image will be kept if you don't upload a new one</p>
            </div>
          )}

          <label>📸 Main Product Image {editingId && "(Leave empty to keep existing)"}</label>
          <input type="file" name="image" onChange={handleFile} accept="image/*" />
          {editingId && (
            <p style={{ fontSize: "12px", color: "#28a745", marginTop: "5px", marginBottom: "15px" }}>
              ✅ Optional: Only upload if you want to replace the image
            </p>
          )}
        </>
      );
    }

    // -----------------------------
    // PROMOTIONS (unchanged)
    // -----------------------------
    if (activeTab === "promotions") {
      return (
        <>
          <label>Title *</label>
          <input name="title" value={form.title || ""} onChange={handleInput} required />

          <label>Subtitle</label>
          <input name="subtitle" value={form.subtitle || ""} onChange={handleInput} placeholder="Optional subtitle" />

          <label>Price</label>
          <input name="price" type="number" value={form.price || ""} onChange={handleInput} placeholder="Current price" />

          <label>Original Price</label>
          <input
            name="originalPrice"
            type="number"
            value={form.originalPrice || ""}
            onChange={handleInput}
            placeholder="Original price (for discount display)"
          />

          <label>Features (แยกบรรทัดละ 1 รายการ)</label>
          <textarea
            name="features"
            value={
              Array.isArray(form.features)
                ? form.features.join("\n")
                : (form.features || "").split(",").map((v) => v.trim()).filter(Boolean).join("\n")
            }
            onChange={(e) => {
              const features = e.target.value.split(/\n|,/).map((v) => v.trim()).filter(Boolean);
              setForm({ ...form, features });
            }}
            rows="4"
            placeholder="ฟรีค่าออกแบบ&#10;รับประกัน 5 ปี&#10;บริการหลังการขายดีเยี่ยม"
          />

          <label>Detailed Features (แยกบรรทัดละ 1 รายการ)</label>
          <textarea
            name="detailedFeatures"
            value={
              Array.isArray(form.detailedFeatures)
                ? form.detailedFeatures.join("\n")
                : (form.detailedFeatures || "").split(",").map((v) => v.trim()).filter(Boolean).join("\n")
            }
            onChange={(e) => {
              const detailedFeatures = e.target.value.split(/\n|,/).map((v) => v.trim()).filter(Boolean);
              setForm({ ...form, detailedFeatures });
            }}
            rows="4"
            placeholder="วัสดุคุณภาพสูงจากยุโรป&#10;ฮาร์ดแวร์นำเข้าจากเยอรมัน&#10;ทีมช่างมืออาชีพ"
          />

          <label>Dimensions</label>
          <input name="dimensions" value={form.dimensions || ""} onChange={handleInput} placeholder="e.g., 240x60x90 cm" />

          <label>Material</label>
          <input name="material" value={form.material || ""} onChange={handleInput} placeholder="e.g., ไม้ปาร์ติเกิลบอร์ดเกรดพรีเมียม" />

          <label>Warranty</label>
          <input name="warranty" value={form.warranty || ""} onChange={handleInput} placeholder="e.g., 5 ปี" />

          <label>Installation</label>
          <input name="installation" value={form.installation || ""} onChange={handleInput} placeholder="e.g., ฟรีค่าติดตั้ง" />

          <label>Active Status</label>
          <select
            name="isActive"
            value={form.isActive !== false}
            onChange={(e) => setForm({ ...form, isActive: e.target.value === "true" })}
          >
            <option value={true}>Active</option>
            <option value={false}>Inactive</option>
          </select>

          {editingId && originalImages && originalImages.image && (
            <div style={{ marginBottom: "15px", marginTop: "20px" }}>
              <label>Current Main Image:</label>
              <div style={{ margin: "10px 0", padding: "10px", backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
                <img
                  src={
                    originalImages.image.startsWith("http")
                      ? originalImages.image
                      : `${API_URL.replace("/api", "")}/${originalImages.image}`
                  }
                  alt="Current Main"
                  style={{ width: "200px", height: "200px", objectFit: "cover", border: "2px solid #ddd", borderRadius: "8px" }}
                />
              </div>
              <p style={{ fontSize: "12px", color: "#666" }}>ℹ️ This main image will be kept if you don't upload a new one</p>
            </div>
          )}

          <label>📸 Main Promotion Image {editingId && "(Leave empty to keep existing)"}</label>
          <input type="file" name="image" onChange={handleFile} accept="image/*" required={!editingId} />
          {editingId && (
            <p style={{ fontSize: "12px", color: "#28a745", marginTop: "5px", marginBottom: "15px" }}>
              ✅ Optional: Only upload if you want to replace the main image
            </p>
          )}

          {editingId && originalImages && originalImages.images && (
            <div style={{ marginBottom: "15px", marginTop: "20px" }}>
              <label>Current Additional Images:</label>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                  margin: "10px 0",
                  padding: "10px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                }}
              >
                {(Array.isArray(originalImages.images) ? originalImages.images : [originalImages.images]).map((img, idx) => {
                  const imgSrc = img.startsWith("http") ? img : `${API_URL.replace("/api", "")}/${img}`;
                  return (
                    <div key={idx} style={{ position: "relative" }}>
                      <img
                        src={imgSrc}
                        alt={`Additional ${idx + 1}`}
                        style={{ width: "100px", height: "100px", objectFit: "cover", border: "2px solid #ddd", borderRadius: "8px" }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          bottom: "5px",
                          right: "5px",
                          backgroundColor: "rgba(0,0,0,0.7)",
                          color: "white",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontSize: "10px",
                        }}
                      >
                        #{idx + 1}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p style={{ fontSize: "12px", color: "#666" }}>ℹ️ These additional images will be preserved unless you upload new ones</p>
            </div>
          )}

          <label>📸 Additional Images (Up to 5) {editingId && "(Leave empty to keep existing)"}</label>
          <input type="file" name="images" multiple onChange={handleFile} accept="image/*" />
          <p style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>ℹ️ Select up to 5 additional images for the promotion gallery</p>
          {editingId && (
            <p style={{ fontSize: "12px", color: "#28a745", marginTop: "5px" }}>
              ✅ Optional: Only upload if you want to replace all additional images
            </p>
          )}
        </>
      );
    }

    // -----------------------------
    // PORTFOLIOS (unchanged)
    // -----------------------------
    if (activeTab === "portfolios") {
      return (
        <>
          <label>Title *</label>
          <input name="title" value={form.title || ""} onChange={handleInput} required />

          <label>Category</label>
          <select name="category" value={form.category || "kitchen"} onChange={handleInput}>
            <option value="kitchen">ชุดครัว (Kitchen)</option>
            <option value="tv-wall">ผนังทีวี (TV Wall)</option>
            <option value="closet">ตู้เสื้อผ้า (Closet)</option>
          </select>

          <label>Location</label>
          <input name="location" value={form.location || ""} onChange={handleInput} placeholder="e.g., Bangkok, Thailand" />

          <label>Area</label>
          <input name="area" value={form.area || ""} onChange={handleInput} placeholder="e.g., 120 sq.m" />

          <label>Year</label>
          <input name="year" value={form.year || new Date().getFullYear().toString()} onChange={handleInput} placeholder="e.g., 2024" />

          <label>Description</label>
          <textarea name="description" value={form.description || ""} onChange={handleInput} rows="4" placeholder="Project description..." />

          <label>Features (แยกบรรทัดละ 1 รายการ)</label>
          <textarea
            name="features"
            value={
              Array.isArray(form.features)
                ? form.features.join("\n")
                : form.features
                  ? form.features
                    .split(/\n|,/)
                    .map((v) => v.trim())
                    .filter(Boolean)
                    .join("\n")
                  : ""
            }
            onChange={(e) =>
              setForm({
                ...form,
                features: e.target.value
                  .split(/\n|,/)
                  .map((v) => v.trim())
                  .filter(Boolean),
              })
            }
            rows="4"
            placeholder="Custom Kitchen\nWalk-in Closet\nSmart Lighting"
          />

          <label>Materials (แยกบรรทัดละ 1 รายการ)</label>
          <textarea
            name="materials"
            value={
              Array.isArray(form.materials)
                ? form.materials.join("\n")
                : form.materials
                  ? form.materials
                    .split(/\n|,/)
                    .map((v) => v.trim())
                    .filter(Boolean)
                    .join("\n")
                  : ""
            }
            onChange={(e) =>
              setForm({
                ...form,
                materials: e.target.value
                  .split(/\n|,/)
                  .map((v) => v.trim())
                  .filter(Boolean),
              })
            }
            rows="4"
            placeholder="Oak Wood\nMarble Countertops\nStainless Steel"
          />

          <label>Featured</label>
          <select name="featured" value={form.featured || false} onChange={(e) => setForm({ ...form, featured: e.target.value === "true" })}>
            <option value={false}>No</option>
            <option value={true}>Yes</option>
          </select>

          <label>Active Status</label>
          <select name="isActive" value={form.isActive !== false} onChange={(e) => setForm({ ...form, isActive: e.target.value === "true" })}>
            <option value={true}>Active</option>
            <option value={false}>Inactive</option>
          </select>

          {editingId && originalImages && originalImages.images && (
            <div style={{ marginBottom: "15px", marginTop: "20px" }}>
              <label>Current Portfolio Images:</label>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                  margin: "10px 0",
                  padding: "10px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                }}
              >
                {(Array.isArray(originalImages.images) ? originalImages.images : [originalImages.images]).map((img, idx) => {
                  const imgSrc = img.startsWith("http") ? img : `${API_URL.replace("/api", "")}/${img}`;
                  return (
                    <div key={idx} style={{ position: "relative" }}>
                      <img
                        src={imgSrc}
                        alt={`Current ${idx + 1}`}
                        style={{ width: "100px", height: "100px", objectFit: "cover", border: "2px solid #ddd", borderRadius: "8px" }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          bottom: "5px",
                          right: "5px",
                          backgroundColor: "rgba(0,0,0,0.7)",
                          color: "white",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontSize: "10px",
                        }}
                      >
                        #{idx + 1}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p style={{ fontSize: "12px", color: "#666" }}>ℹ️ These images will be preserved unless you upload new ones</p>
            </div>
          )}

          <label>📸 Portfolio Images สูงสุด 4 รูป {editingId && "(Leave empty to keep existing)"}</label>
          <input type="file" name="images" multiple onChange={handleFile} accept="image/*" required={!editingId} />
          {editingId && <p style={{ fontSize: "12px", color: "#28a745", marginTop: "5px" }}>✅ Optional: Only upload if you want to replace all images</p>}
        </>
      );
    }

    return <p>Select a tab to continue.</p>;
  };

  return (
    <div className="admin-container">
      <h1>Admin Dashboard</h1>

      <div className="tabs">
        {["promotions", "products", "portfolios"].map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              setShowForm(false);
              setEditingId(null);
              setForm({});
              setOriginalImages(null);
              setFormKey((prev) => prev + 1);
            }}
            className={activeTab === tab ? "active" : ""}
          >
            {tab.toUpperCase()}
          </button>
        ))}
      </div>

      <button
        className="add-btn"
        onClick={() => {
          setShowForm(!showForm);
          if (showForm) {
            setEditingId(null);
            setForm({});
            setOriginalImages(null);
          } else {
            setFormKey((prev) => prev + 1);
          }
        }}
      >
        {showForm ? "✕ CANCEL" : `+ Add ${activeTab.slice(0, -1)}`}
      </button>

      {showForm && (
        <form key={formKey} className="admin-form" onSubmit={handleSubmit}>
          <h3>{editingId ? `Edit ${activeTab.slice(0, -1)}` : `Add New ${activeTab.slice(0, -1)}`}</h3>
          {renderFormFields()}
          <button type="submit" className="save-btn" disabled={loading}>
            {loading ? "⏳ Processing..." : editingId ? "💾 UPDATE" : "✨ CREATE"}
          </button>
        </form>
      )}

      <h2>{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} List</h2>
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <p>Loading...</p>
        </div>
      ) : (
        <>
          <p style={{ textAlign: "center", marginBottom: "20px", color: "#666" }}>Total: {data.length} items</p>
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Title</th>
                  <th>{activeTab === "portfolios" ? "Location" : "Price"}</th>
                  <th>{activeTab === "portfolios" ? "Year" : activeTab === "products" ? "Category" : "Subtitle"}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: "center", padding: "2rem" }}>
                      No {activeTab} found. Click "Add {activeTab.slice(0, -1)}" to create one.
                    </td>
                  </tr>
                ) : (
                  data.map((item, idx) => (
                    <TableRow
                      key={item._id}
                      item={item}
                      itemIndex={idx}
                      activeTab={activeTab}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
